import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';

interface ApprovalDetails {
  requesterName: string | null;
  email: string;
  unitLabel: string | null;
  businessUnitName: string | null;
  iaReason: string | null;
}

type PageState =
  | 'loading'
  | 'ready'
  | 'approved'
  | 'rejected'
  | 'invalid'
  | 'already_reviewed'
  | 'error';

/**
 * RegistrationApprovalPage - Confirmação de titularidade de novo cadastro
 *
 * Acessada pelo responsável a partir do link enviado por e-mail quando a
 * validação automática do documento não confirma a titularidade.
 */
export function RegistrationApprovalPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [details, setDetails] = useState<ApprovalDetails | null>(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.body.className = 'basic-layout';
    document.documentElement.style.backgroundColor = '#EDF5F7';
    return () => {
      document.body.className = 'fix-header fix-sidebar page-water home';
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    if (!token) return;

    api
      .get(`/api/v1/registration/approve/${token}`)
      .then((response) => {
        setDetails({
          requesterName: response.data?.requester_name ?? null,
          email: response.data?.email,
          unitLabel: response.data?.unit_label ?? null,
          businessUnitName: response.data?.business_unit_name ?? null,
          iaReason: response.data?.ia_reason ?? null,
        });
        setReason(response.data?.ia_reason ?? '');
        setState('ready');
      })
      .catch((err: unknown) => {
        const error =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : undefined;
        setState(error === 'already_reviewed' ? 'already_reviewed' : 'invalid');
      });
  }, [token]);

  const handleApprove = async () => {
    if (!token) return;
    setIsSubmitting(true);

    try {
      await api.post(`/api/v1/registration/approve/${token}`);
      setState('approved');
    } catch {
      setState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;
    setIsSubmitting(true);

    try {
      await api.post(`/api/v1/registration/reject/${token}`, { reason });
      setState('rejected');
    } catch {
      setState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-mist-100">
      <div className="
        relative
        bg-gradient-to-br from-navy-900 to-navy-700
        h-[190px] max-[812px]:h-[80px]
        mb-[10px]
        py-[20px]
      ">
        <div className="text-center">
          <img src="/images/logo_login.png" alt="Laager" className="max-[812px]:h-[55px]" />
        </div>
      </div>

      <div className="flex justify-center p-[20px]">
        <div className="w-full max-w-[380px] text-center bg-white rounded-[16px] shadow-[0_10px_30px_rgba(17,33,51,0.08)] px-[26px] py-[32px]">
          {state === 'loading' && (
            <p className="text-[13px] text-navy-700/70">Carregando…</p>
          )}

          {state === 'invalid' && (
            <>
              <div className="text-navy-800 text-[17px] font-bold mb-[6px]">Link inválido</div>
              <p className="text-[12px] text-navy-700/70 leading-[1.5]">
                Este link de aprovação é inválido ou expirou.
              </p>
            </>
          )}

          {state === 'already_reviewed' && (
            <>
              <div className="text-navy-800 text-[17px] font-bold mb-[6px]">Cadastro já analisado</div>
              <p className="text-[12px] text-navy-700/70 leading-[1.5]">
                Esta solicitação já foi revisada anteriormente.
              </p>
            </>
          )}

          {state === 'approved' && (
            <>
              <div className="text-navy-800 text-[17px] font-bold mb-[6px]">Acesso aprovado</div>
              <p className="text-[12px] text-navy-700/70 leading-[1.5]">
                O solicitante foi notificado e já pode confirmar o cadastro.
              </p>
            </>
          )}

          {state === 'rejected' && (
            <>
              <div className="text-navy-800 text-[17px] font-bold mb-[6px]">Acesso recusado</div>
              <p className="text-[12px] text-navy-700/70 leading-[1.5]">
                O solicitante foi notificado por e-mail de que o documento não foi aprovado.
              </p>
            </>
          )}

          {state === 'error' && (
            <p className="text-[12px] text-alert-caution">
              Não foi possível concluir a operação. Tente novamente em instantes.
            </p>
          )}

          {state === 'ready' && details && (
            <>
              <div className="text-navy-800 text-[17px] font-bold mb-[6px]">
                Análise de titularidade
              </div>
              <p className="text-[12px] text-navy-700/70 mb-[20px] leading-[1.5]">
                Confirme se o solicitante abaixo tem direito de acesso à unidade antes de aprovar.
              </p>

              <div className="text-left text-[13px] text-navy-800 bg-mist-100 rounded-[10px] px-[16px] py-[14px] mb-[22px] space-y-[6px]">
                {details.requesterName && (
                  <div><b>Solicitante:</b> {details.requesterName}</div>
                )}
                <div><b>E-mail:</b> {details.email}</div>
                {details.unitLabel && <div><b>Unidade:</b> {details.unitLabel}</div>}
                {details.businessUnitName && (
                  <div><b>Empreendimento:</b> {details.businessUnitName}</div>
                )}
              </div>

              <div className="text-left mb-[22px]">
                <label
                  htmlFor="ia-reason"
                  className="block text-[12px] font-bold text-navy-800 mb-[6px]"
                >
                  Motivo da análise automática
                </label>
                <p className="text-[11px] text-navy-700/60 mb-[8px] leading-[1.4]">
                  Você pode editar este texto. Ele será enviado ao solicitante caso o acesso seja recusado.
                </p>
                <textarea
                  id="ia-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={isSubmitting}
                  rows={4}
                  className="
                    w-full text-[13px] text-navy-800
                    bg-mist-100 rounded-[10px] px-[14px] py-[10px]
                    border border-mist-300 focus:border-navy-700 focus:outline-none
                    resize-y
                    disabled:opacity-50
                  "
                />
              </div>

              <button
                type="button"
                onClick={handleApprove}
                disabled={isSubmitting}
                className="
                  w-full mx-auto block
                  py-[11px] px-[20px]
                  bg-lime-500 hover:bg-lime-400
                  !text-navy-900 text-[13px] font-bold
                  border-0 rounded-[20px]
                  text-center cursor-pointer
                  transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isSubmitting ? 'Aprovando…' : 'Aprovar acesso'}
              </button>

              <button
                type="button"
                onClick={handleReject}
                disabled={isSubmitting}
                className="
                  w-full mx-auto block mt-[10px]
                  py-[11px] px-[20px]
                  bg-transparent hover:bg-alert-caution/10
                  !text-alert-caution text-[13px] font-bold
                  border border-alert-caution rounded-[20px]
                  text-center cursor-pointer
                  transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isSubmitting ? 'Recusando…' : 'Recusar acesso'}
              </button>
            </>
          )}

          {(state === 'approved' || state === 'rejected' || state === 'invalid' || state === 'already_reviewed') && (
            <div className="text-center mt-4">
              <Link
                to="/login"
                className="
                  inline-flex items-center justify-center gap-2 w-full
                  py-[11px] px-[20px]
                  bg-mist-100 hover:bg-mist-300/60
                  !text-navy-800 text-[13px] font-medium
                  border-0 rounded-[20px]
                  transition-colors
                "
              >
                Ir para o login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
